/**
 * Reusable empty state with icon, title, subtitle, optional action button.
 *
 * Usage:
 *   <EmptyState
 *     icon="weather"
 *     title={t('dashboard.noData')}
 *     subtitle="Start by adding your first field"
 *     action={{ label: "Add Field", onClick: () => {} }}
 *   />
 */

const ICONS = {
  weather: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
    </svg>
  ),
  fields: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
    </svg>
  ),
  chat: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  data: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
};

export default function EmptyState({ icon, title, subtitle, action, testId = 'empty-state' }) {
  return (
    <div
      data-testid={testId}
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center text-center py-12 px-4"
    >
      <div
        aria-hidden="true"
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-[var(--aif-text-muted)]"
      >
        {ICONS[icon] || ICONS.data}
      </div>
      {title && (
        <h3 data-testid={`${testId}-title`} className="text-sm font-semibold text-[var(--aif-text-primary)] mb-1">
          {title}
        </h3>
      )}
      {subtitle && (
        <p data-testid={`${testId}-message`} className="text-xs text-[var(--aif-text-muted)] max-w-[240px] mb-4">
          {subtitle}
        </p>
      )}
      {action && (
        <button
          data-testid={`${testId}-cta`}
          onClick={action.onClick}
          className="aif-touch-target px-4 py-2 text-sm font-medium rounded-lg bg-[var(--aif-color-primary)] text-white hover:opacity-90 transition"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
